/* eslint-disable max-lines-per-function */
import {
  Autocomplete,
  Button,
  FormHelperText,
  Grid,
  TextField,
} from "@mui/material";
import { IExportFilters } from "../../interfaces/order";
import { compareDates, formatSlaThreshold } from "../../utils/orders";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import {
  DATE_FORMAT,
  ERROR_FROM_GREATER_THAN_TO,
  ERROR_TO_GREATER_THAN_FROM,
  LOCALE,
  SLA_ALERT_LEVEL_CONFIG,
  SLA_ALERT_LEVEL_THRESHOLDS,
} from "../../utils/constants";

const SLA_ALL_OPTION = { id: "all", label: "Todos" };
const slaAlertLevelOptions = [
  SLA_ALL_OPTION,
  ...Object.entries(SLA_ALERT_LEVEL_CONFIG).map(([id, { label }]) => {
    const threshold = SLA_ALERT_LEVEL_THRESHOLDS[id];
    const thresholdLabel = threshold
      ? formatSlaThreshold(threshold.amount, threshold.unit)
      : "";
    return { id, label: label + thresholdLabel };
  }),
];
const allSlaLevelIds = Object.keys(SLA_ALERT_LEVEL_CONFIG);

const showInputErrors = (inputErrors: string[]) =>
  inputErrors.map((message: string) => (
    <FormHelperText className="color-error" key={message}>
      {message}
    </FormHelperText>
  ));

const ExportFilters = ({
  values,
  setValues,
  carriers,
  handleSubmit,
  errors,
  setErrors,
  merchants,
  showstatusFilter,
  aliases,
  clients,
}: IExportFilters) => {
  const validateDates = (keyName: string, date: string | null) => {
    const { fromDate, toDate } = values;
    const isDateValid = compareDates(
      keyName === "fromDate" ? date : fromDate,
      keyName === "fromDate" ? toDate : date,
    );
    setErrors({
      ...errors,
      invalidFrom: keyName === "fromDate" && !isDateValid,
      invalidTo: keyName === "toDate" && !isDateValid,
    });
  };
  const handleChangeDate = (dateType: string, newValue: any) => {
    validateDates(dateType, newValue);
    setValues({ ...values, [dateType]: newValue });
  };
  return (
    <Grid container spacing={2} className="m-t-10">
      <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale={LOCALE}>
        <Grid item xs={12} md={2}>
          <DatePicker
            label="Desde"
            value={values.fromDate || null}
            onChange={(newValue: any) => {
              if (!newValue?.invalid) handleChangeDate("fromDate", newValue);
            }}
            inputFormat={DATE_FORMAT}
            renderInput={(params: any) => (
              <TextField
                fullWidth
                size="small"
                {...params}
                error={errors.invalidFrom}
              />
            )}
            disableFuture
          />
          <FormHelperText className="color-error" key="error-message">
            {errors.fromDate?.length > 0 && showInputErrors(errors.fromDate)}
            {errors.invalidFrom && ERROR_FROM_GREATER_THAN_TO}
          </FormHelperText>
        </Grid>
        <Grid item xs={12} md={2}>
          <DatePicker
            label="Hasta"
            value={values.toDate || null}
            onChange={(newValue: any) => {
              if (!newValue?.invalid) handleChangeDate("toDate", newValue);
            }}
            inputFormat={DATE_FORMAT}
            renderInput={(params: any) => (
              <TextField
                fullWidth
                size="small"
                {...params}
                error={errors.invalidTo}
              />
            )}
            disableFuture
          />
          <FormHelperText className="color-error" key="error-message">
            {errors.toDate?.length > 0 && showInputErrors(errors.toDate)}
            {errors.invalidTo && ERROR_TO_GREATER_THAN_FROM}
          </FormHelperText>
        </Grid>
      </LocalizationProvider>
      {showstatusFilter && (
        <Grid item xs={12} md={6} lg={2}>
          <Autocomplete
            id="status"
            inputValue={values.status}
            options={aliases || []}
            noOptionsText="No se encontró el estado"
            onChange={(_, value: any, reason: string) =>
              setValues({
                ...values,
                status: reason === "clear" ? "" : value.id,
              })
            }
            renderInput={(params) => (
              <TextField {...params} fullWidth size="small" label="Estado" />
            )}
          />
        </Grid>
      )}
      <Grid item xs={12} md={2}>
        <Autocomplete
          getOptionLabel={(option) => option.name}
          options={carriers}
          value={values.carrier}
          noOptionsText="No se encontró el operador logístico"
          onChange={(_, value: any, reason: string) =>
            setValues({
              ...values,
              carrier:
                reason === "clear"
                  ? undefined
                  : { name: value.name, _id: value._id },
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              size="small"
              label="Operador Logístico"
              error={errors.carrier && errors.carrier.length > 0}
            />
          )}
        />
        {errors.carrier &&
          errors.carrier?.length > 0 &&
          showInputErrors(errors?.carrier)}
      </Grid>
      {merchants && merchants.length > 0 && (
        <Grid item xs={12} md={2}>
          <Autocomplete
            inputValue={values?.merchantName}
            getOptionLabel={(option) => option.merchant.entity.name}
            options={merchants}
            noOptionsText="No se encontró el merchant"
            onChange={(_, value) =>
              setValues({
                ...values,
                merchantId: value?.merchantId,
                merchantName: value?.merchant.entity.name,
              })
            }
            renderInput={(params) => (
              <TextField {...params} fullWidth size="small" label="Merchant" />
            )}
          />
        </Grid>
      )}
      {clients && clients.length > 0 && (
        <Grid item xs={12} md={2}>
          <Autocomplete
            inputValue={values?.clientName}
            getOptionLabel={(option) => option.name}
            options={clients}
            noOptionsText="No se encontró el cliente"
            onChange={(_, value) =>
              setValues({
                ...values,
                clientId: Number(value?.id),
                clientName: value?.name,
              })
            }
            renderInput={(params) => (
              <TextField {...params} fullWidth size="small" label="Cliente" />
            )}
          />
        </Grid>
      )}
      <Grid item xs={12} md={2}>
        <Autocomplete
          multiple
          options={slaAlertLevelOptions}
          getOptionLabel={(option) => option.label}
          value={slaAlertLevelOptions.filter((o) => values.sla_alert_level?.includes(o.id))}
          noOptionsText="No se encontró el nivel"
          onChange={(_, selectedOptions) => {
            const hasAll = selectedOptions.some((o) => o.id === SLA_ALL_OPTION.id);
            let slaLevel: string[] | undefined;
            if (hasAll) slaLevel = allSlaLevelIds;
            else if (selectedOptions.length > 0) slaLevel = selectedOptions.map((o) => o.id);
            setValues({ ...values, sla_alert_level: slaLevel });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              size="small"
              label="Nivel de SLA"
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        <Button
          fullWidth
          variant="contained"
          size="small"
          sx={{ padding: "8px" }}
          onClick={handleSubmit}
        >
          Generar archivo
        </Button>
      </Grid>
    </Grid>
  );
};

export default ExportFilters;
